import React, { useContext, useEffect, useState } from "react";
import once from "lodash.once";
import { useLocation } from "@tanstack/react-location";
import { SDKs } from "../../constants/routes";
import { useStateManagerContext } from "../stateManager";
import {
  sdkLocales,
  LCTranslationKeys,
  DTranslationKeys,
} from "./sdkLocales";

interface LanguagesProviderProps {
  children: React.ReactNode;
}

type TranslationKeys = LCTranslationKeys | DTranslationKeys;

interface DefaultContext<T extends TranslationKeys> {
  translations: { [key in T]: string };
}

// Using once to get around generic Context issue: https://stackoverflow.com/a/61020816/4282290
const createLangaugesContext = once(<T extends TranslationKeys>() =>
  React.createContext<DefaultContext<T>>({} as DefaultContext<T>)
);

const useLangauges = <T extends TranslationKeys>() => {
  const context = useContext(createLangaugesContext<T>());

  if (!context) {
    throw new Error("useLangauges must be used under LanguagesProvider");
  }

  return context;
};

const LanguagesProvider = <T extends LCTranslationKeys | DTranslationKeys>({
  children,
}: LanguagesProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [translations, setTranslations] = useState<{ [key in T]: string }>();
  const { sdkState } = useStateManagerContext();
  const locale = sdkState.globalOptions?.locale || "en";
  const LangaugeContext = createLangaugesContext<T>();

  const location = useLocation();
  const pathSegments = location.current.pathname.split("/");
  const currentRoute = pathSegments[pathSegments.length - 1] as SDKs;
  const getSdkTranslations = sdkLocales[currentRoute];

  useEffect(() => {
    setIsLoading(true);

    getSdkTranslations(locale)
      .then((r) => {
        setTranslations(r.translations as { [key in T]: string });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [locale, getSdkTranslations]);

  if (isLoading) {
    return null;
  }

  if (!translations) {
    throw new Error("No translations were found.");
  }

  return (
    <LangaugeContext.Provider
      value={{
        translations,
      }}
    >
      {children}
    </LangaugeContext.Provider>
  );
};

export { LanguagesProvider, useLangauges };
export * from "./sdkLocales";
